import type { FirebaseApp } from 'firebase/app';
import type { FirestoreSettings, QueryConstraint } from 'firebase/firestore';

import type { ConstraintSpec } from '@elara/firebolt/shared';

import type { ConstraintBuilders } from './firestore-constraints.js';

import {
  connectFirestoreEmulator,
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
} from 'firebase/firestore';
import * as fb from 'firebase/firestore';

import { createContainer } from '@elara/core/di';

import { translateConstraintSpecsWith } from './firestore-constraints.js';

export interface FirestoreConfig extends FirestoreSettings {
  /**
   * Whether to enable local cache for the Firestore database.
   * @default false
   */
  enableLocalCache?: boolean;
}
/**
 * Creates a container for essential Firebase services required by Firebolt.
 * This allows for easy mocking during testing.
 */
export function createFirestoreContainer(app: FirebaseApp, config: FirestoreConfig | undefined) {
  const $firestore = createContainer('firestore')
    .provided('config', config)
    .dependent('localCache', ['config'], ({ config }) => {
      // `persistentLocalCache` + `persistentMultipleTabManager` are IndexedDB-backed (web-only) and
      // are absent/throw in Firebase's React Native build. They are only needed when local cache is
      // enabled (web), so build them here rather than in a standalone provider the eager DI `.use`
      // would resolve on RN. Mobile leaves `enableLocalCache` unset → memory cache, no IndexedDB.
      return config?.enableLocalCache
        ? persistentLocalCache({ tabManager: persistentMultipleTabManager() })
        : undefined;
    })
    .dependent('backend', ['config', 'localCache'], ({ config, localCache }) => {
      return initializeFirestore(app, { ...config, localCache });
    })
    .providedLazy('collection', () => fb.collection)
    .providedLazy('collectionGroup', () => fb.collectionGroup)
    .providedLazy('doc', () => fb.doc)
    .providedLazy('getDoc', () => fb.getDoc)
    .providedLazy('getDocs', () => fb.getDocs)
    .providedLazy('onSnapshot', () => fb.onSnapshot)
    .providedLazy('query', () => fb.query)
    .providedLazy('where', () => fb.where)
    .providedLazy('orderBy', () => fb.orderBy)
    .providedLazy('limit', () => fb.limit)
    .providedLazy('limitToLast', () => fb.limitToLast)
    .providedLazy('startAt', () => fb.startAt)
    .providedLazy('startAfter', () => fb.startAfter)
    .providedLazy('endBefore', () => fb.endBefore)
    .providedLazy('endAt', () => fb.endAt)
    .fn('translateConstraints', translateConstraintSpecs)
    // Emulator wiring lives on the container so each SDK connects its own — `firebase/firestore`'s
    // `connectFirestoreEmulator` only accepts a JS `Firestore` (ELARA-982/981).
    .dependent('connectEmulator', ['backend'], ({ backend }) => {
      return (host: string, port: number): void => connectFirestoreEmulator(backend, host, port);
    });
  return $firestore;
}

/** The JS-SDK constraint translation (web). The ordering logic is shared — see the module above. */
export function translateConstraintSpecs(constraints: ConstraintSpec[]): QueryConstraint[] {
  return translateConstraintSpecsWith<QueryConstraint>(
    constraints,
    fb as unknown as ConstraintBuilders<QueryConstraint>,
  );
}

export type $FirestoreContainer = ReturnType<typeof createFirestoreContainer>;
export type FirestoreContainer = $FirestoreContainer['use'];
